import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'

import { StyledAlert, StyledAlertIcon, StyledMessage, StyledTitle } from './useNotification.styles'

type NotificationType = 'error' | 'success'

type NotificationContextType = {
  message: string | ReactNode
  title?: string
  type: NotificationType
  showNotification: (msg: string | ReactNode, type: NotificationType, title?: string) => void
  hideNotification: () => void
  open: boolean
}

const NotificationContext = createContext<NotificationContextType>({
  message: '',
  title: '',
  type: 'error',
  showNotification: () => {
    // eslint-disable-next-line no-console
    console.warn('showNotification function not initialized')
  },
  hideNotification: () => {
    // eslint-disable-next-line no-console
    console.warn('hideNotification function not initialized')
  },
  open: false,
})

type NotificationProps = {
  open: boolean
  message: string | ReactNode
  title?: string
  type: NotificationType
  onClose: () => void
}

const Notification: React.FC<NotificationProps> = ({ open, message, type, onClose, title }) => {
  return (
    <Snackbar open={open} autoHideDuration={3000} onClose={onClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <StyledAlert
        onClose={onClose}
        severity={type}
        iconMapping={{
          success: (
            <StyledAlertIcon>
              <CheckCircleOutlineIcon fontSize="inherit" />
            </StyledAlertIcon>
          ),
        }}
        action={
          <IconButton aria-label="close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <StyledTitle variant="h2">{title}</StyledTitle>
        <StyledMessage variant="body1">{message}</StyledMessage>
      </StyledAlert>
    </Snackbar>
  )
}

type NotificationProviderProps = {
  children: React.ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState<string | ReactNode>('')
  const [type, setType] = useState<NotificationType>('error')
  const [title, setTitle] = useState<string | undefined>(undefined)

  const showNotification = useCallback((msg: string | ReactNode, t: NotificationType, notificationTitle?: string) => {
    setMessage(msg)
    setType(t)
    setOpen(true)
    setTitle(notificationTitle)
  }, [])

  const hideNotification = useCallback(() => {
    setOpen(false)
    setTimeout(() => {
      setTitle('')
    }, 500)
  }, [])

  const contextValue = useMemo(
    () => ({
      message,
      type,
      title,
      open,
      showNotification,
      hideNotification,
    }),
    [message, type, title, open, hideNotification, showNotification],
  )

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Notification open={open} message={message} type={type} title={title} onClose={hideNotification} />
    </NotificationContext.Provider>
  )
}

export const useNotification = (): NotificationContextType => {
  return useContext(NotificationContext)
}
